/*
  Millie & Jones: estimated delivery dates, and the earliest bookable delivery date.

  Fills every <span class="mj-eta" hidden> rendered by snippets/mj-delivery-estimate.liquid
  with a delivery window worked out in the shopper's browser, so cached pages never show
  a stale date and nothing extra is asked of the server.

  Lead time, in working days, comes from (first match wins):
    1. data-min / data-max (product metafields custom.lead_time_min / custom.lead_time_max)
    2. the product's existing delivery text (custom.delivery), e.g.
       "Free delivery in 4 to 14 working days", "Made to order, with free delivery in 3 to 4 weeks",
       "Free weekday delivery on a date you choose in your basket", "1-3 days"
    3. the "next day delivery" tag

  Rules, matching the basket date picker (snippets/cart-delivery-date.liquid):
    - "Today" and the 12pm cutoff are UK time (Europe/London), whatever the shopper's clock zone.
    - Working days are Monday to Friday, excluding England and Wales bank holidays.
    - An order after 12pm, or at the weekend or on a bank holiday, counts from the next working day.

  v18 (12 Oct 2026):
    - MJETA.firstBookable(): the date the basket's date picker picks for the shopper (its firstSelectableDate),
      for express checkout on the product page and in the "Added to your basket" pop-ups (assets/mj-express.js).
      Same rules, same bank holidays. RULES below must match the basket's "Delivery date" settings
      (templates/cart.json, section mj_cart): minimum 1 day, maximum 120 days, 12:00 cutoff on, restock buffer 2.
    - Booked wording, for suppliers that book the day and a 3-hour slot with the customer (data-booked, set by
      snippets/mj-delivery-estimate.liquid) and a range of days: under the price
      "Free delivery as soon as this Friday" then "Estimated 16-30 Oct, in a 3-hour slot we book with you"
      ("Est." when that line would wrap); cards and the sticky bar "Delivery from 16 Oct".

  No dependencies. Safe to load on every page: it does nothing if there are no .mj-eta elements.
*/
(function () {
  'use strict';
  if (window.MJETA) return;

  // England and Wales bank holidays, same list as snippets/cart-delivery-date.liquid
  // (source: https://www.gov.uk/bank-holidays). Add supplier closure dates here too,
  // e.g. a Christmas shutdown, so estimates step over them.
  var CLOSED = (
    '2026-01-01,2026-04-03,2026-04-06,2026-05-04,2026-05-25,2026-08-31,2026-12-25,2026-12-28,' +
    '2027-01-01,2027-03-26,2027-03-29,2027-05-03,2027-05-31,2027-08-30,2027-12-27,2027-12-28,' +
    '2028-01-03,2028-04-14,2028-04-17,2028-05-01,2028-05-29,2028-08-28,2028-12-25,2028-12-26'
  ).split(',');
  var CUTOFF_HOUR = 12;
  var DAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  var FULLDAY = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  var MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  // The basket's date picker settings (templates/cart.json, section mj_cart). Keep in step.
  var RULES = { minDays: 1, maxDays: 120, cutoffHour: 12, cutoffEnabled: true, restockBuffer: 2 };

  // v18: milliseconds to add to the device clock. assets/mj-express.js sets it from the store's Date header
  // when the device is more than a minute out, so a phone with a wrong clock can't book a day already gone.
  var clockOffset = 0;
  function setClock(ms) { clockOffset = isFinite(ms) && Math.abs(ms) > 60000 ? ms : 0; }

  function ukParts() {
    var now = new Date(Date.now() + clockOffset);
    try {
      var parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Europe/London', year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23'
      }).formatToParts(now);
      var o = {};
      for (var i = 0; i < parts.length; i++) o[parts[i].type] = parts[i].value;
      return { y: +o.year, mo: +o.month, d: +o.day, h: +o.hour, mi: +o.minute, s: +o.second };
    } catch (e) {
      return { y: now.getFullYear(), mo: now.getMonth() + 1, d: now.getDate(), h: now.getHours(), mi: now.getMinutes(), s: now.getSeconds() };
    }
  }

  function ukNow() {
    var p = ukParts();
    return { date: new Date(p.y, p.mo - 1, p.d), hour: p.h };
  }

  // v18: milliseconds until the next 12:00 UK time, when the earliest bookable date moves on
  function msToCutoff() {
    var p = ukParts();
    var left = CUTOFF_HOUR * 3600 - (p.h * 3600 + p.mi * 60 + p.s);
    if (left <= 0) left += 86400;
    return left * 1000 + 500;
  }

  function iso(d) {
    var m = d.getMonth() + 1, day = d.getDate();
    return d.getFullYear() + '-' + (m < 10 ? '0' : '') + m + '-' + (day < 10 ? '0' : '') + day;
  }

  function isWorkingDay(d) {
    var w = d.getDay();
    return w !== 0 && w !== 6 && CLOSED.indexOf(iso(d)) === -1;
  }

  function addWorkingDays(from, n) {
    var d = new Date(from.getTime());
    for (var found = 0, guard = 0; found < n && guard < 400; guard++) {
      d.setDate(d.getDate() + 1);
      if (isWorkingDay(d)) found++;
    }
    return d;
  }

  function nextOpenDay(d) {
    var c = new Date(d.getTime());
    for (var guard = 0; !isWorkingDay(c) && guard < 30; guard++) c.setDate(c.getDate() + 1);
    return c;
  }

  // Reads a lead time from the delivery wording already on the product.
  function parseText(text, nextDayTag) {
    var t = (text || '').toLowerCase();
    var m;
    if (/choose|chosen date|select your/.test(t)) return { mode: 'choose', min: 1, max: 1 };
    if ((m = t.match(/(\d+)\s*(?:-|to|–)\s*(\d+)\s*weeks?/))) {
      return { mode: /made to order/.test(t) ? 'made' : 'range', min: +m[1] * 5, max: +m[2] * 5 };
    }
    if ((m = t.match(/from\s+(\d+)\s*(?:working|business)?\s*days?/))) return { mode: 'from', min: +m[1], max: +m[1] };
    if ((m = t.match(/(\d+)\s*(?:-|to|–)\s*(\d+)\s*(?:working|business)?\s*days?/))) {
      return { mode: /made to order/.test(t) ? 'made' : 'range', min: +m[1], max: +m[2] };
    }
    if (/next (?:working )?day/.test(t) || nextDayTag) return { mode: 'choose', min: 1, max: 1 };
    return null;
  }

  // Works out the window. Exposed as MJETA.compute for testing.
  function compute(min, max, mode) {
    var now = ukNow();
    // v7 (11 Oct 2026): a weekend or bank holiday order is handled like one placed after the 12pm
    // cutoff on the last working day. Nothing is processed until the next working day, which then
    // counts as day 0, so at the weekend (as after Friday 12pm) the earliest next-day date is Tuesday.
    var late = now.hour >= CUTOFF_HOUR || !isWorkingDay(now.date);
    var start, end;
    if (mode === 'choose') {
      // Same floor as the basket picker: next day before 12pm on a working day, otherwise one more working day.
      start = late ? addWorkingDays(now.date, min + 1) : nextOpenDay(addDays(now.date, min));
      end = start;
    } else {
      var extra = late ? 1 : 0;
      start = addWorkingDays(now.date, min + extra);
      end = addWorkingDays(now.date, Math.max(max, min) + extra);
    }
    return { start: start, end: end, mode: mode || 'range', today: now.date };
  }

  function addDays(d, n) {
    var c = new Date(d.getTime());
    c.setDate(c.getDate() + n);
    return c;
  }

  function long(d) { return DAY[d.getDay()] + ' ' + d.getDate() + ' ' + MON[d.getMonth()]; }
  function short(d) { return d.getDate() + ' ' + MON[d.getMonth()]; }
  function shortRange(a, b) {
    if (a.getTime() === b.getTime()) return short(a);
    if (a.getMonth() === b.getMonth()) return a.getDate() + ' to ' + short(b);
    return short(a) + ' to ' + short(b);
  }
  // v18: "16-30 Oct", "27 Oct-10 Nov". A plain hyphen, never a dash.
  function hyphenRange(a, b) {
    if (a.getTime() === b.getTime()) return short(a);
    if (a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear()) return a.getDate() + '-' + short(b);
    return short(a) + '-' + short(b);
  }
  // v18: "tomorrow", "this Friday" (later in the same Monday to Friday week), otherwise "Mon 19 Oct"
  function soon(d, today) {
    var days = Math.round((d - today) / 864e5);
    if (days === 1) return 'tomorrow';
    if (days > 1 && days < 7 && today.getDay() + days <= 5) return 'this ' + FULLDAY[d.getDay()];
    return long(d);
  }

  // Wording per place. No dashes, British dates.
  function words(w, context) {
    var a = w.start, b = w.end;
    if (w.booked && w.mode === 'range') {
      // v18 booked wording: the first date sells, the range keeps it honest
      if (context === 'card' || context === 'sticky') return 'Delivery from ' + short(a);
      if (context === 'price') {
        return { head: 'Free delivery as soon as ' + soon(a, w.today), rest: ' ' + hyphenRange(a, b) + ', in a 3-hour slot we book with you' };
      }
    }
    switch (context) {
      case 'card':
        if (w.mode === 'choose' || w.mode === 'from') return 'Delivery from ' + short(a);
        return 'Delivery ' + shortRange(a, b);
      case 'sticky':
        if (w.mode === 'choose' || w.mode === 'from') return 'Delivery from ' + short(a);
        return 'Delivery ' + shortRange(a, b);
      case 'price': // v16: leads with Free; weekdays only where the basket date picker charges for weekends
        if (w.mode === 'choose') return 'Free weekday delivery, choose from ' + long(a);
        if (w.mode === 'from') return 'Free delivery, usually from ' + long(a);
        if (w.mode === 'made') return 'Made to order. Free delivery, usually ' + long(a) + ' to ' + long(b);
        return 'Free delivery, usually ' + long(a) + ' to ' + long(b);
      case 'cart':
        if (w.mode === 'choose') return 'Choose your delivery day below, from ' + long(a);
        if (w.mode === 'from') return 'Estimated delivery from ' + long(a);
        return 'Estimated delivery ' + long(a) + ' to ' + long(b);
      case 'cart-est': // basket without the date picker (v7)
        if (w.mode === 'choose' || w.mode === 'from') return 'Estimated delivery from ' + long(a);
        return 'Estimated delivery ' + long(a) + ' to ' + long(b);
      default: // product page: [label, dates]
        if (w.mode === 'choose') return ['Choose your delivery day in the basket', 'From ' + long(a)];
        if (w.mode === 'from') return ['Estimated delivery', 'From ' + long(a)];
        if (w.mode === 'made') return ['Made to order. Estimated delivery', long(a) + ' to ' + long(b)];
        return ['Estimated delivery', long(a) + ' to ' + long(b)];
    }
  }

  // v18: the second booked line says "Estimated" when it fits on one line, "Est." when that would wrap
  function fitEst(el) {
    var sub = el.querySelector('.mj-eta__sub');
    var est = sub && sub.querySelector('.mj-eta__est');
    if (!est) return;
    est.textContent = 'Estimated';
    var h = sub.getBoundingClientRect().height;
    if (!h) return; // not on screen yet: try again on the next resize or font load
    var lh = parseFloat(window.getComputedStyle(sub).lineHeight) || 17;
    if (h > lh * 1.5) est.textContent = 'Est.';
  }
  // Re-fit when a booked line appears or changes width (a variant coming back in stock shows it again)
  var fitObserver = null;
  function observeFit(el) {
    if (!('ResizeObserver' in window)) return;
    if (!fitObserver) {
      fitObserver = new ResizeObserver(function (entries) {
        entries.forEach(function (en) {
          var w = Math.round(en.contentRect.width);
          if (!w || en.target._mjFitW === w) return; // hidden (a sold-out size): leave it as it is
          en.target._mjFitW = w;
          var el = en.target;
          (window.requestAnimationFrame || setTimeout)(function () {
            try { fitEst(el); } catch (e) { /* leave it */ }
          });
        });
      });
    }
    fitObserver.observe(el);
  }
  function fitAll() {
    var els = document.querySelectorAll('.mj-eta[data-eta-booked]');
    for (var i = 0; i < els.length; i++) {
      try { fitEst(els[i]); } catch (e) { /* leave it */ }
    }
  }

  function fill(el) {
    var d = el.dataset;
    var lead = null;
    if (d.min) {
      var min = parseInt(d.min, 10), max = parseInt(d.max || d.min, 10);
      if (!isNaN(min)) lead = { mode: d.mode || (min === 1 && max === 1 ? 'choose' : 'range'), min: min, max: isNaN(max) ? min : max };
    }
    if (!lead) lead = parseText(d.text, d.nextday === 'true');
    if (!lead) { el.setAttribute('data-eta', 'none'); return; }
    if (d.mode && !d.min) lead.mode = d.mode;
    var w = compute(lead.min, lead.max, lead.mode);
    w.booked = d.booked === 'true';
    var out = words(w, d.context || 'pdp');
    if (typeof out === 'string') {
      el.textContent = out;
    } else if (out && out.head) {
      // v18 booked wording: a bold line, then the estimate in smaller type
      el.textContent = '';
      var two = document.createElement('span');
      two.className = 'mj-eta__two';
      var head = document.createElement('b');
      head.className = 'mj-eta__head';
      head.textContent = out.head;
      var sub = document.createElement('small');
      sub.className = 'mj-eta__sub';
      var est = document.createElement('span');
      est.className = 'mj-eta__est';
      est.textContent = 'Estimated';
      sub.appendChild(est);
      sub.appendChild(document.createTextNode(out.rest));
      two.appendChild(head);
      two.appendChild(sub);
      el.appendChild(two);
      el.setAttribute('data-eta-booked', '');
    } else {
      // Product page: a small label line, then the dates in bold
      el.textContent = '';
      var label = document.createElement('span');
      label.className = 'mj-eta__label';
      label.textContent = out[0];
      var dates = document.createElement('strong');
      dates.className = 'mj-eta__dates';
      dates.textContent = out[1];
      el.appendChild(label);
      el.appendChild(dates);
    }
    el.setAttribute('data-eta', iso(w.start) + '/' + iso(w.end));
    el.removeAttribute('hidden');
    if (el.hasAttribute('data-eta-booked')) {
      fitEst(el);
      observeFit(el);
    }
  }

  function run(root) {
    var els = (root || document).querySelectorAll('.mj-eta:not([data-eta])');
    for (var i = 0; i < els.length; i++) {
      try { fill(els[i]); } catch (e) { /* leave it hidden */ }
    }
  }

  /* ---------- v18: the earliest bookable date (the basket picker's firstSelectableDate) ---------- */

  // Component-wise, like the picker: new Date('2026-10-13') would be UTC midnight.
  function parseISO(value) {
    if (!value) return null;
    var p = String(value).split('-');
    if (p.length !== 3) return null;
    var d = new Date(+p[0], +p[1] - 1, +p[2]);
    return isNaN(d.getTime()) ? null : d;
  }

  // "13 Oct 2026", the line item format the picker writes (its lineItemFormat)
  function lineFormat(d) {
    return (d.getDate() < 10 ? '0' : '') + d.getDate() + ' ' + MON[d.getMonth()] + ' ' + d.getFullYear();
  }

  // The store's clock as a basket section renders it ('now' in the shop's time zone, Europe/London)
  function storeNow(dateISO, hour) {
    var d = parseISO(dateISO), h = parseInt(hour, 10);
    return d && !isNaN(h) ? { date: d, hour: h } : null;
  }

  // The picker's rules for one basket or product. o: now ({date, hour}, default UK time now), blocked
  // (",2026-12-24," extra closed dates, added to the bank holidays), restock (YYYY-MM-DD, a restock date
  // the floor counts from; ignored unless still to come), sat / sun (true when the collection opens that day).
  function dateRules(o) {
    o = o || {};
    var num = function (v, dflt) { var n = parseInt(v, 10); return isNaN(n) ? dflt : n; };
    var now = o.now || ukNow();
    var midnight = now.date;
    var blocked = CLOSED.slice();
    String(o.blocked || '').split(',').forEach(function (s) {
      s = s.trim();
      if (s && blocked.indexOf(s) === -1) blocked.push(s);
    });
    var minDays = num(o.minDays, RULES.minDays);
    var maxDays = num(o.maxDays, RULES.maxDays);
    var cutoffHour = num(o.cutoffHour, RULES.cutoffHour);
    var cutoffEnabled = o.cutoffEnabled == null ? RULES.cutoffEnabled : !!o.cutoffEnabled;
    var buffer = num(o.restockBuffer, RULES.restockBuffer);
    var excluded = [];
    if (!o.sun) excluded.push(0);
    if (!o.sat) excluded.push(6);
    function isWeekend(d) { var w = d.getDay(); return w === 0 || w === 6; }
    function isBlocked(d) { return blocked.indexOf(iso(d)) !== -1; }
    function isExcludedDay(d) { return excluded.indexOf(d.getDay()) !== -1; }
    function isOpen(d) { return !isWeekend(d) && !isExcludedDay(d) && !isBlocked(d); } // the picker's isWorkingDay
    function addOpen(from, n) {
      var c = from, found = 0;
      for (var i = 0; i < 366 && found < n; i++) {
        c = addDays(c, 1);
        if (isOpen(c)) found++;
      }
      return found < n ? addDays(from, n) : c;
    }
    var late = (cutoffEnabled && !isNaN(cutoffHour) && now.hour >= cutoffHour) || !isOpen(midnight);
    var workingMin = late ? addOpen(midnight, minDays + 1) : addDays(midnight, minDays);
    var openedMin = addDays(midnight, (late ? minDays + 1 : minDays) + 1);
    var restock = parseISO(o.restock);
    if (restock && restock > midnight) {
      var floor = addDays(restock, buffer);
      if (floor > workingMin) workingMin = floor;
      if (floor > openedMin) openedMin = floor;
    }
    var minDate = openedMin < workingMin ? openedMin : workingMin;
    var maxDate = addDays(midnight, maxDays);
    if (maxDate <= minDate) maxDate = addDays(minDate, maxDays);
    function belowFloor(d) { return d < (isWeekend(d) && !isExcludedDay(d) ? openedMin : workingMin); }
    return { isWeekend: isWeekend, isBlocked: isBlocked, isExcludedDay: isExcludedDay, belowFloor: belowFloor, workingMin: workingMin, maxDate: maxDate };
  }

  // The first date the picker would choose for the shopper: never a weekend, never closed, never below a floor.
  function firstBookable(o) {
    var r = dateRules(o);
    var c = r.workingMin;
    for (var i = 0; i < 400; i++) {
      if (c > r.maxDate) return null;
      if (!r.isWeekend(c) && !r.isExcludedDay(c) && !r.isBlocked(c) && !r.belowFloor(c)) return c;
      c = addDays(c, 1);
    }
    return null;
  }

  // A date already chosen: would the picker still accept it? (its isBelowFloor / isExcluded test)
  function isBookable(d, o) {
    if (!d) return false;
    var r = dateRules(o);
    return !r.belowFloor(d) && !r.isBlocked(d) && !r.isExcludedDay(d);
  }

  window.MJETA = {
    compute: compute, parse: parseText, run: run,
    words: words, fit: fitAll,
    rules: RULES, closed: CLOSED, ukNow: ukNow, iso: iso, long: long, parseISO: parseISO, lineFormat: lineFormat,
    storeNow: storeNow, firstBookable: firstBookable, isBookable: isBookable, setClock: setClock, msToCutoff: msToCutoff
  };

  function start() {
    run();
    // Cards added by filtering, the basket refreshing and the sticky bar re-rendering.
    if ('MutationObserver' in window) {
      var queued = false;
      new MutationObserver(function () {
        if (queued) return;
        queued = true;
        (window.requestAnimationFrame || setTimeout)(function () { queued = false; run(); });
      }).observe(document.body, { childList: true, subtree: true });
    }
    // The booked line's fit depends on the width and the web font
    var timer = null;
    window.addEventListener('resize', function () { clearTimeout(timer); timer = setTimeout(fitAll, 150); });
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(fitAll);
    window.addEventListener('load', fitAll);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
